/**
 * Base do recebimento (ADR 0046, Entrega 1): critérios de recebimento de cada item, validados por
 * Compras antes do pedido, e responsável técnico pelo aceite. Usado pela API e pelas telas.
 * Nada aqui registra recebimento ou aceite, nem é aprovação ou alçada.
 */
import { z } from 'zod';
import { fromScaled, toScaled } from './money.ts';
import { decimalString, uuid } from './schemas.ts';

export const ACCEPTANCE_MODES = ['completion', 'milestones', 'periods'] as const;
export type AcceptanceMode = (typeof ACCEPTANCE_MODES)[number];

export const PERIOD_FREQUENCIES = ['weekly', 'monthly', 'quarterly', 'yearly'] as const;
export type PeriodFrequency = (typeof PERIOD_FREQUENCIES)[number];

/** fixed: a mesma quantidade em cada período; variable: varia e depende de regra específica. */
export const PERIOD_QUANTITY_RULES = ['fixed', 'variable'] as const;
export type PeriodQuantityRule = (typeof PERIOD_QUANTITY_RULES)[number];

export interface AcceptanceMilestone {
  description: string;
  quantity: string;
}

/**
 * Os critérios de um item, como a validação os guarda e compara. `quantity` é a quantidade de
 * referência: a contratada (pedido ou proposta aprovada) quando houver; antes disso, a pedida.
 */
export interface ReceivingCriteriaItem {
  itemId: string;
  kind: 'product' | 'service';
  quantity: string;
  unit: string;
  acceptanceMode: AcceptanceMode | null;
  milestones: AcceptanceMilestone[];
  periodFrequency: PeriodFrequency | null;
  periodStart: string | null;
  periodEnd: string | null;
  periodQuantityRule: PeriodQuantityRule | null;
  quantityPerPeriod: string | null;
  requiresAssetRegistration: boolean;
}

/** Forma única dos critérios (quantidades com 4 casas), para comparar a validação com o atual. */
export function normalizeCriteria(
  items: readonly ReceivingCriteriaItem[],
): ReceivingCriteriaItem[] {
  const qty = (v: string) => fromScaled(toScaled(v));
  return items.map((i) => ({
    itemId: i.itemId,
    kind: i.kind,
    quantity: qty(i.quantity),
    unit: i.unit,
    acceptanceMode: i.kind === 'service' ? i.acceptanceMode : null,
    milestones:
      i.kind === 'service' && i.acceptanceMode === 'milestones'
        ? i.milestones.map((m) => ({ description: m.description, quantity: qty(m.quantity) }))
        : [],
    periodFrequency: i.acceptanceMode === 'periods' ? i.periodFrequency : null,
    periodStart: i.acceptanceMode === 'periods' ? i.periodStart : null,
    periodEnd: i.acceptanceMode === 'periods' ? i.periodEnd : null,
    periodQuantityRule: i.acceptanceMode === 'periods' ? i.periodQuantityRule : null,
    quantityPerPeriod:
      i.acceptanceMode === 'periods' && i.periodQuantityRule === 'fixed' && i.quantityPerPeriod
        ? qty(i.quantityPerPeriod)
        : null,
    requiresAssetRegistration: i.kind === 'product' && i.requiresAssetRegistration,
  }));
}

/** A validação mais recente ainda vale? (mesmos itens, na mesma ordem, com os mesmos critérios) */
export function criteriaMatch(
  validated: readonly ReceivingCriteriaItem[],
  current: readonly ReceivingCriteriaItem[],
): boolean {
  return (
    JSON.stringify(normalizeCriteria(validated)) === JSON.stringify(normalizeCriteria(current))
  );
}

export type CriteriaIssue =
  | 'mode_required'
  | 'milestones_required'
  | 'milestones_total'
  | 'period_required'
  | 'period_order'
  | 'period_rule_required'
  | 'period_quantity_required'
  | 'period_quantity_division'
  | 'period_count';

const MONTHS_PER_STEP: Record<PeriodFrequency, number> = {
  weekly: 0,
  monthly: 1,
  quarterly: 3,
  yearly: 12,
};

/** Data N passos depois do início (meses somados sobre o início, como o PostgreSQL faz). */
function stepFrom(start: string, frequency: PeriodFrequency, n: number): string {
  const [y, m, d] = start.split('-').map(Number) as [number, number, number];
  if (frequency === 'weekly') {
    return new Date(Date.UTC(y, m - 1, d + 7 * n)).toISOString().slice(0, 10);
  }
  const months = m - 1 + MONTHS_PER_STEP[frequency] * n;
  const year = y + Math.floor(months / 12);
  const month = ((months % 12) + 12) % 12;
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return new Date(Date.UTC(year, month, Math.min(d, last))).toISOString().slice(0, 10);
}

/** Quantos períodos começam entre o início e o fim (inclusive), na periodicidade dada. */
export function countPeriods(start: string, end: string, frequency: PeriodFrequency): number {
  let n = 0;
  while (n < 10_000 && stepFrom(start, frequency, n) <= end) n += 1;
  return n;
}

/** O que impede validar os critérios de um item (vazio = pode validar). */
export function criteriaIssues(item: ReceivingCriteriaItem): CriteriaIssue[] {
  if (item.kind !== 'service') return [];
  const issues: CriteriaIssue[] = [];
  if (!item.acceptanceMode) return ['mode_required'];
  if (item.acceptanceMode === 'milestones') {
    if (item.milestones.length === 0) issues.push('milestones_required');
    else {
      const total = item.milestones.reduce((sum, m) => sum + toScaled(m.quantity), 0n);
      if (total !== toScaled(item.quantity)) issues.push('milestones_total');
    }
  }
  if (item.acceptanceMode === 'periods') {
    if (!item.periodFrequency || !item.periodStart) return [...issues, 'period_required'];
    if (item.periodEnd && item.periodEnd < item.periodStart) return [...issues, 'period_order'];
    if (!item.periodQuantityRule) return [...issues, 'period_rule_required'];
    // Quantidade variável por período: não há conta automática (precisa de regra específica).
    if (item.periodQuantityRule === 'fixed') {
      if (!item.quantityPerPeriod || toScaled(item.quantityPerPeriod) <= 0n) {
        issues.push('period_quantity_required');
      } else {
        const total = toScaled(item.quantity);
        const each = toScaled(item.quantityPerPeriod);
        if (total % each !== 0n) issues.push('period_quantity_division');
        else if (
          item.periodEnd &&
          BigInt(countPeriods(item.periodStart, item.periodEnd, item.periodFrequency)) * each !==
            total
        ) {
          issues.push('period_count');
        }
      }
    }
  }
  return issues;
}

export type CriteriaWarning = 'period_variable_rule' | 'period_open_ended';

/** Avisos que não impedem validar, mas pedem atenção (ou uma regra que ainda não existe). */
export function criteriaWarnings(item: ReceivingCriteriaItem): CriteriaWarning[] {
  if (item.kind !== 'service' || item.acceptanceMode !== 'periods') return [];
  const warnings: CriteriaWarning[] = [];
  if (item.periodQuantityRule === 'variable') warnings.push('period_variable_rule');
  if (!item.periodEnd) warnings.push('period_open_ended');
  return warnings;
}

/**
 * Quem fica impedido de ser responsável técnico (D3, N3): o comprador responsável, quem escolheu
 * a proposta e quem emitiu um pedido ativo da compra.
 */
export function technicalOwnerImpeded(
  userId: string,
  ctx: { buyerUserId: string | null; proposalDecidedBy: string | null; orderIssuers: string[] },
): boolean {
  return (
    userId === ctx.buyerUserId ||
    userId === ctx.proposalDecidedBy ||
    ctx.orderIssuers.includes(userId)
  );
}

// ----- Entradas -----

const quantity = decimalString(10, 4).refine(
  (v) => Number(v) > 0,
  'A quantidade precisa ser maior que zero',
);

const milestoneInput = z.object({
  description: z.string().trim().min(1).max(300),
  quantity,
});

export const receivingCriteriaItemInput = z.object({
  itemId: uuid,
  acceptanceMode: z.enum(ACCEPTANCE_MODES).nullable().default(null),
  milestones: z.array(milestoneInput).max(50).default([]),
  periodFrequency: z.enum(PERIOD_FREQUENCIES).nullable().default(null),
  periodStart: z.iso.date().nullable().default(null),
  periodEnd: z.iso.date().nullable().default(null),
  periodQuantityRule: z.enum(PERIOD_QUANTITY_RULES).nullable().default(null),
  quantityPerPeriod: decimalString(10, 4).nullable().default(null),
  requiresAssetRegistration: z.boolean().default(false),
});

/** Compras grava e valida os critérios de todos os itens de uma vez. */
export const receivingCriteriaInput = z.object({
  items: z.array(receivingCriteriaItemInput).min(1).max(200),
  /** Obrigatória quando a compra já tem pedido ao fornecedor (mudança depois do pedido). */
  justification: z
    .string()
    .trim()
    .max(2000)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
});
export type ReceivingCriteriaInput = z.infer<typeof receivingCriteriaInput>;

export const technicalOwnerInput = z.object({
  userId: uuid,
  reason: z.string().trim().min(5, 'Explique por que esta pessoa').max(1000),
});

/** Situações em que os critérios e o responsável técnico ainda podem mudar. */
const RECEIVING_LOCKED = ['awaiting_need_approval', 'rejected', 'completed', 'cancelled'];

/** O que quem compra pode fazer com os critérios e o responsável técnico agora. */
export function receivingActions(requestStatus: string, buyer: boolean, hasActiveOrder: boolean) {
  const open = buyer && !RECEIVING_LOCKED.includes(requestStatus);
  return {
    /** Gravar e validar os critérios (com justificativa depois do pedido). */
    validate: open,
    justificationRequired: hasActiveOrder,
    /**
     * Depois do pedido, os critérios de aceite dos serviços não mudam aqui: uma justificativa não
     * substitui aprovação contratual ou financeira (depende da governança e do Jurídico).
     */
    serviceCriteriaLocked: hasActiveOrder,
    /** Indicar outro responsável técnico. */
    designate: open,
  };
}
